import { Directive, ElementRef, forwardRef, HostListener } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MAT_INPUT_VALUE_ACCESSOR } from '@angular/material/input';


/**
 * Remove non-numeric digits of input
 * 
 * if you need also to parse the inputed value to Number, use IntegerDirective 
 * @see [IntegerDirective](integer.directive.ts)
 * 
 * @ex
 * ```html
 * <!-- value1 is a number -->
 * <input integer [(ngModel)]="value1"/>
 * <!-- value2 is a string of numeric digits -->
 * <input numberOnly [(ngModel)]="value2"/>
 * ```
 */
@Directive({
  selector: 'input[numberOnlyInput]',
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NumberOnlyInputDirective),
      multi: true,
    },
    {
      provide: MAT_INPUT_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NumberOnlyInputDirective),
    },
  ]
})
export class NumberOnlyInputDirective implements ControlValueAccessor {
  private _userInteracted = false;

  constructor(private hostElement: ElementRef) { }
 
  private _formatValue(value: string) {
    if (!value) {
      return null;
    }

    const valueFixed = value.replace(/\D/g, "");

    this.hostElement.nativeElement.value = valueFixed;
    this._onChange(valueFixed);
  }
  
  @HostListener('input', [])
  onInput() {
    this.userInteracted = true;

    const value = this.hostElement.nativeElement.value as string;

    this._formatValue(value);
  }

  registerOnChange(fn: any): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  writeValue(value: any): void {
    if (!this.userInteracted) {
      this.hostElement.nativeElement.value = value ?? '';
      return;
    }

    this._formatValue(value)
  }

  get userInteracted() {
    return this._userInteracted;
  }

  set userInteracted(value: boolean) {
    this._userInteracted = value;
  }

  private _onChange: (value: any) => void = () => {};
  private _onTouched: () => void = () => {};
}
